/*eslint max-lines-per-function: ["error", 220]*/
import React, { useState, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { GetDictionaryDto as Dictionary } from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { PokCoreContextType } from '../../../common/pokCore';

import { Selector, Option, OptionWithLang } from './Selector';

export interface DictionarySelectorProps {
  readOnly?: boolean;
  value?: string;
  dictionary: string;
  onChange?: (option: OptionWithLang) => void;
  maxDepth?: number;
  clearable?: boolean;
  showInfoIcon?: boolean;
  infoIconTitle?: string;
  sortFunction?: (a: Dictionary, b: Dictionary) => number;
  allowableValues?: Dictionary[];
}

const getDictValueAsync = async (
  values: Dictionary[],
  id: string,
  pok: PokCoreContextType,
) => {
  const vals = values.filter(v => v.id === id);
  if (vals.length === 1) {
    return vals[0];
  }

  try {
    return await pok.dictionaries.getById(id);
  } catch {
    throw new Error('Dictionary value id is not OK: ' + id);
  }
};

const sublevel = async (
  values: Dictionary[],
  dictId: string,
  pok: PokCoreContextType,
  maxDepth?: number,
) => {
  if (maxDepth) {
    let level = 1;
    let id: string | undefined = dictId;
    while (id) {
      try {
        const value = await getDictValueAsync(values, id, pok);
        if (!value.parentId) {
          id = undefined;
        } else {
          id = value.parentId;
          level++;
        }
      } catch {
        break;
      }
    }

    if (level === maxDepth) {
      return false;
    }
  }

  const sublevelCount = values.filter(
    v => v.parentId === dictId && v.active,
  ).length;
  return sublevelCount > 0;
};

const useVisibleLevels = (
  selectedEntries: OptionWithLang[],
  values: Dictionary[],
  pok: PokCoreContextType,
  maxDepth?: number,
) => {
  const [levels, setLevels] = useState<
    { parentId: string | null; label: string; badge: string | undefined }[]
  >([{ parentId: null, label: '', badge: undefined }]);

  useEffect(() => {
    if (!values.length) {
      setLevels([{ parentId: null, label: '', badge: undefined }]);
      return;
    }

    const loadLevels = async () => {
      try {
        if (selectedEntries.length === 0) {
          setLevels([{ parentId: null, label: '', badge: undefined }]);
          return;
        }

        const newLevels = await Promise.all(
          selectedEntries.map(async entry => {
            try {
              const dictValue = await getDictValueAsync(
                values,
                entry.value,
                pok,
              );
              return {
                parentId: dictValue.parentId || null,
                label: entry.label,
                badge: entry.badge,
              };
            } catch {
              return {
                parentId: null,
                label: entry.label,
                badge: entry.badge,
              };
            }
          }),
        );

        const last = selectedEntries[selectedEntries.length - 1];
        try {
          if (await sublevel(values, last.value, pok, maxDepth)) {
            newLevels.push({ parentId: last.value, label: '', badge: '' });
          }
        } catch {
          // Ignore sublevel errors
        }

        const finalLevels = maxDepth ? newLevels.slice(0, maxDepth) : newLevels;
        setLevels(finalLevels);
      } catch (error) {
        console.error('Error in useVisibleLevels:', error);
        // Fallback to basic display
        setLevels([{ parentId: null, label: '', badge: undefined }]);
      }
    };

    void loadLevels();
  }, [selectedEntries, values, pok, maxDepth]);

  return levels;
};

export default (props: DictionarySelectorProps) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [values, setValues] = useState<Dictionary[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<OptionWithLang[]>([]);
  const [selectedOption, setSelectedOption] = useState<OptionWithLang>();
  const [language, setLanguage] = useState<string>();
  const [allValues, setAllValues] = useState<OptionWithLang[]>([]);

  useEffect(() => {
    setLanguage(pok.getLanguage());
  }, [pok]);

  const provider = useMemo(
    () => (parentId: string | null) => {
      return async (text: string) => {
        let ourValues = values
          .filter(v => v.parentId === parentId)
          .filter(v =>
            language === 'PL'
              ? v.value?.toLocaleLowerCase().includes(text.toLocaleLowerCase())
              : v.valueEn
                  ?.toLocaleLowerCase()
                  .includes(text.toLocaleLowerCase()),
          )
          .filter(v => v.active);

        if (props.sortFunction) {
          ourValues = ourValues.sort(props.sortFunction);
        }

        setAllValues(
          ourValues.map(v => {
            return {
              label: language === 'PL' ? v.value : v.valueEn || '',
              labelPl: v.value,
              labelEn: v.valueEn || '',
              value: v.id || '',
              ...(v.shortname && { badge: v.shortname }),
            };
          }),
        );
        return ourValues.map(v => {
          return {
            label: language === 'PL' ? v.value : v.valueEn || '',
            value: v.id || '',
            ...(v.shortname && { badge: v.shortname }),
          };
        });
      };
    },
    [values, language, props.sortFunction],
  );

  const selectEntry = async (option: Option, parentId: string | null) => {
    const optionWithLang =
      allValues.find(o => o.value === option.value) ||
      (option as OptionWithLang);
    const newSelectedEntries: OptionWithLang[] = [];
    // all levels above current selection are still there
    // all below we lose
    for (const selected of selectedEntries) {
      try {
        const dictValue = await getDictValueAsync(values, selected.value, pok);
        if (dictValue.parentId !== parentId) {
          newSelectedEntries.push(selected);
        } else {
          break;
        }
      } catch {
        break;
      }
    }

    if (option.value) {
      newSelectedEntries.push(optionWithLang);
    }

    setSelectedEntries(newSelectedEntries);

    const leaf = !(await sublevel(values, option.value, pok, props.maxDepth));
    if (leaf && option.value) {
      setSelectedOption(optionWithLang);
      if (props.onChange) {
        props.onChange(optionWithLang);
      }
    } else {
      const nv = null as unknown as string;
      setSelectedOption({ value: nv, label: nv, labelPl: nv, labelEn: nv });
      if (newSelectedEntries.length === 0 && props.onChange) {
        //for the first level, we inform about clearing the value (no need to leave the field).
        // In the case of lower levels, we allow manipulation of values until the last option is selected or the field is left
        props.onChange(optionWithLang);
      }
    }
  };

  useEffect(() => {
    if (!values.length) {
      return;
    }

    const buildEntries = async () => {
      const entries: OptionWithLang[] = [];
      let dictId: string | undefined = props.value;
      while (dictId) {
        try {
          const val = await getDictValueAsync(values, dictId, pok);
          entries.unshift({
            value: dictId,
            label: language === 'PL' ? val.value : val.valueEn || '',
            labelPl: val.value,
            labelEn: val.valueEn || '',
            badge: val.shortname,
          });
          dictId = val.parentId;
        } catch {
          dictId = undefined;
        }
      }

      setSelectedEntries(entries);
    };

    void buildEntries();
  }, [props.value, values, language, pok]);

  useEffect(() => {
    /// TODO: we could optimize it in the near future
    /// getting data from database could be on start and after change
    let mounted = true;
    if (props.allowableValues) {
      setValues(props.allowableValues);
    } else {
      pok.dictionaries
        .getByDictionaryType(props.dictionary)
        .then(values => {
          if (mounted) {
            setValues(values);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }

    return () => {
      mounted = false;
    };
  }, [
    props.dictionary,
    pok.dictionaries,
    notifications,
    language,
    props.allowableValues,
  ]);

  const visibleLevelsData = useVisibleLevels(
    selectedEntries,
    values,
    pok,
    props.maxDepth,
  );

  if (!values.length) {
    return <Skeleton />;
  }

  const blurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    const focusInsideDiv = event.currentTarget.contains(event.relatedTarget);
    if (selectedOption && props.onChange && !focusInsideDiv) {
      if (props.value || selectedOption.value) {
        props.onChange(selectedOption);
      }
    }
  };

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
    <div onBlur={blurHandler}>
      {visibleLevelsData.map(level => (
        <Selector
          key={language + (level.parentId || 'zero-level')}
          className={level.parentId ? 'mt-1' : ''}
          readOnly={props.readOnly}
          creatable={false}
          provider={provider(level.parentId)}
          value={level.label}
          valueToShow={true}
          valueBadge={level.badge}
          onChange={option =>
            void selectEntry(option as Option, level.parentId)
          }
          clearable={props.clearable}
          showInfoIcon={props.showInfoIcon}
          infoIconTitle={props.infoIconTitle}
        />
      ))}
    </div>
  );
};
